/**
 * HitlReport (I5 · Figma 128:218) — post-escalation report.
 *
 * Fires once, after handback. Captures the single most-useful signal for
 * the content team: what the AI tutor should have caught but didn't. The
 * report row gets routed to the content team through the pending-report
 * partial index on hitl_reports.
 *
 * Design decision: this is a very short form on purpose — a longer
 * template would encourage instructors to skip it. One text box + submit.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hitlApi } from '../../services/hitlApi';
import { styles } from './HitlStyles';

const HitlReport: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!id) return;
    setSubmitting(true);
    setError(null);
    try {
      await hitlApi.submitReport({
        session_id: id,
        ai_missed_note: note.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (!id) return <div style={styles.page}>Missing session id.</div>;

  return (
    <div style={{ ...styles.page, maxWidth: 640 }}>
      <h1 style={styles.h1}>Post-escalation report</h1>
      <p style={styles.subtitle}>
        Optional. Any signal for the content team about what the AI tutor missed?
      </p>

      <div style={styles.card}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>What did the AI miss?</div>
        <div style={{ ...styles.muted, marginBottom: 12 }}>
          Free-form. Content team reads this to tune system prompts + lesson chunks.
        </div>
        <textarea
          placeholder="e.g. 'AI kept steering toward async/await when the actual bug was a class-method binding issue.'"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ ...styles.input, minHeight: 200 }}
        />
      </div>

      {error && (
        <div
          role="alert"
          style={{ ...styles.card, background: '#FEF2F2', borderColor: '#FCA5A5', color: '#B91C1C' }}
        >
          {error}
        </div>
      )}

      {done && (
        <div
          role="status"
          style={{ ...styles.card, background: '#F0FDF4', borderColor: '#86EFAC', color: '#15803D' }}
        >
          Report saved. Content team will triage.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button
          style={{ ...styles.button, ...styles.secondary }}
          onClick={() => nav('/hitl')}
        >
          {done ? 'Back to queue' : 'Skip'}
        </button>
        {!done && (
          <button
            disabled={submitting}
            style={{ ...styles.button, ...styles.primary, opacity: submitting ? 0.6 : 1 }}
            onClick={handleSubmit}
          >
            {submitting ? 'Saving…' : 'Save report'}
          </button>
        )}
      </div>
    </div>
  );
};

export default HitlReport;
